import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Images, X } from "lucide-react";
import useCuratedGallery from "@/lib/useCuratedGallery";

// -----------------------------------------------------------------------------
//  CuratedGallerySection
//
//  Public-facing gallery section rendered on each /hotels/:slug page.
//  Data source: `useCuratedGallery(hotelSlug)` — hits the public endpoint
//  which already filters out `hidden: true` images and returns them in the
//  exact order set by the marketing team in /admin/curated-galleries. The
//  same hook is used by HotelDetailPage to toggle the "Gallery" anchor in
//  the sticky nav — so both stay in sync without a second round-trip.
//
//  Design decisions
//  ────────────────
//  • The whole section renders nothing when the curated gallery is empty
//    (avoids an empty "Gallery" heading on hotels that haven't been curated
//    yet). No CTA, no placeholder — silent no-op.
//  • Only the first `INITIAL_VISIBLE` images render as tiles; a "See all"
//    button opens the lightbox on image #0. Photographers frequently curate
//    hundreds of shots and we don't want a 100-tile wall on the marketing
//    page.
// -----------------------------------------------------------------------------

// Grid arithmetic ─ 2 rows on desktop (5 cols × 2 = 10) reads best next to
// the surrounding editorial sections. Everything above that spills into
// the lightbox.
const INITIAL_VISIBLE = 10;

const t = (lang, en, fr, es) =>
  lang === "fr" ? fr : lang === "es" ? es : en;

// ─────────────────────────────────────────────────────────────────────────
//  Lightbox
// ─────────────────────────────────────────────────────────────────────────
const Lightbox = ({ images, startIdx, onClose, title }) => {
  const [idx, setIdx] = useState(startIdx);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % images.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + images.length) % images.length);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [images.length, onClose]);

  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);
  const next = () => setIdx((i) => (i + 1) % images.length);
  const img = images[idx];

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#1A1513]/95 flex items-center justify-center px-3 md:px-12"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="curated-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 md:top-6 md:right-6 text-[#FDFBF7]/80 hover:text-[#FDFBF7] p-2"
        data-testid="curated-lightbox-close"
        aria-label="Close gallery"
      >
        <X className="w-6 h-6" strokeWidth={1.5} />
      </button>
      <button
        onClick={prev}
        className="absolute left-1 md:left-8 top-1/2 -translate-y-1/2 text-[#FDFBF7]/70 hover:text-[#FDFBF7] p-3"
        data-testid="curated-lightbox-prev"
        aria-label="Previous image"
      >
        <ChevronLeft className="w-7 h-7 md:w-8 md:h-8" strokeWidth={1.5} />
      </button>
      <button
        onClick={next}
        className="absolute right-1 md:right-8 top-1/2 -translate-y-1/2 text-[#FDFBF7]/70 hover:text-[#FDFBF7] p-3"
        data-testid="curated-lightbox-next"
        aria-label="Next image"
      >
        <ChevronRight className="w-7 h-7 md:w-8 md:h-8" strokeWidth={1.5} />
      </button>
      <div className="max-w-6xl w-full">
        <img
          src={img.url}
          alt={img.name || title}
          loading="lazy"
          decoding="async"
          className="w-full max-h-[72vh] md:max-h-[78vh] object-contain"
          data-testid="curated-lightbox-image"
        />
        <div className="mt-4 md:mt-6 flex items-center justify-between text-[#FDFBF7]/85">
          <p className="font-serif-x text-lg md:text-2xl truncate max-w-[70%]">{title}</p>
          <p className="text-[10px] md:text-xs tracking-[0.25em] uppercase tabular-nums">
            {String(idx + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
          </p>
        </div>
        <div className="mt-3 md:mt-4 flex gap-1.5 md:gap-2 overflow-x-auto pb-1">
          {images.map((im, i) => (
            <button
              key={im.id}
              onClick={() => setIdx(i)}
              className={`shrink-0 w-16 h-12 md:w-20 md:h-14 overflow-hidden border-2 transition-opacity ${
                i === idx
                  ? "border-[#D4A373] opacity-100"
                  : "border-transparent opacity-60 hover:opacity-100"
              }`}
              data-testid={`curated-lightbox-thumb-${i}`}
              aria-label={`Go to image ${i + 1}`}
            >
              <img
                src={im.url}
                alt=""
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
//  Section
// ─────────────────────────────────────────────────────────────────────────
const CuratedGallerySection = ({ hotelSlug, hotelName, lang = "en", images: imagesProp, loading: loadingProp }) => {
  // Fetch our own copy only when the parent hasn't supplied one. Keeps
  // the component stand-alone usable elsewhere while avoiding a duplicate
  // round-trip when HotelDetailPage already has the data (parent uses
  // `useCuratedGallery` to decide whether to show the sticky-nav anchor).
  const fetched = useCuratedGallery(imagesProp === undefined ? hotelSlug : null);
  const images = imagesProp !== undefined ? imagesProp : fetched.images;
  const loading = loadingProp !== undefined ? loadingProp : fetched.loading;
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const visible = useMemo(() => images.slice(0, INITIAL_VISIBLE), [images]);
  const remainder = images.length - visible.length;

  // Silent no-op when the curated gallery is empty — keeps the page tight
  // for hotels that haven't been curated yet.
  if (loading || images.length === 0) return null;

  const title = t(lang, "Photo gallery", "Galerie photos", "Galería");
  const overline = t(lang, "GALLERY", "GALERIE", "GALERÍA");
  const intro = t(
    lang,
    "A curated selection of visuals from our team on the ground.",
    "Une sélection d’images capturées par notre équipe sur place.",
    "Una selección de imágenes capturadas por nuestro equipo sobre el terreno."
  );
  const seeAllLabel = t(
    lang,
    `See all ${images.length} photos`,
    `Voir les ${images.length} photos`,
    `Ver las ${images.length} fotos`
  );

  return (
    <section
      id="gallery"
      className="py-20 md:py-28 bg-[#1A1513] text-[#FDFBF7]"
      data-testid="section-curated-gallery"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header — matches the visual language of the sibling editorial
            sections on the hotel page (overline · title · intro on 12-col).
            On the dark surface, the overline keeps its terracotta accent
            and the title lifts to cream for strong contrast. */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-10 md:mb-14">
          <div className="md:col-span-4">
            <p className="overline text-[#C16542]">{overline}</p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl tracking-tight leading-tight text-[#FDFBF7]">
              {title}
            </h2>
          </div>
          <p className="md:col-span-7 md:col-start-6 text-base md:text-lg text-[#FDFBF7]/70 leading-relaxed">
            {intro}
          </p>
        </div>

        {/* Grid — 2 cols mobile, 3 tablet, 5 desktop. Each tile stays a
            square so the composition reads uniformly regardless of the
            original aspect ratios coming out of Bunny. Placeholder bg
            uses a lifted dark tone so the loading state blends in. */}
        <div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-3"
          data-testid="curated-gallery-grid"
        >
          {visible.map((image, i) => (
            <button
              type="button"
              key={image.id}
              onClick={() => setLightboxIdx(i)}
              className="group relative aspect-square overflow-hidden bg-[#2C2621] focus:outline-none focus:ring-2 focus:ring-[#D4A373]"
              data-testid={`curated-gallery-tile-${i}`}
              // The curated gallery photos live in their own admin CRUD
              // (/admin/curated-galleries) — they are NOT tracked in the
              // shared `image_slots` collection. Opt every tile out of
              // AutoImageEditor so the /admin/images tracker does not
              // spawn duplicate auto-hash slots for images that already
              // have a dedicated editing surface.
              data-no-cms-edit="true"
              aria-label={
                image.name
                  ? t(lang, `Open photo: ${image.name}`, `Ouvrir la photo : ${image.name}`, `Abrir foto: ${image.name}`)
                  : t(lang, "Open photo", "Ouvrir la photo", "Abrir foto")
              }
            >
              <img
                src={image.url}
                alt={image.name || hotelName || title}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                onError={(e) => {
                  e.currentTarget.style.opacity = "0.35";
                }}
              />
              {/* Subtle top-down darken on hover — grounds the tile inside
                  the dark section without competing with the photo. */}
              <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          ))}
        </div>

        {/* Call-to-action to reveal the full set inside the lightbox — only
            shown when there is actually more to reveal. Uses the warm
            gold accent that sits above the deep chocolate background. */}
        {remainder > 0 ? (
          <div className="mt-8 md:mt-10 flex justify-center">
            <button
              type="button"
              onClick={() => setLightboxIdx(0)}
              className="inline-flex items-center gap-2 border border-[#FDFBF7]/25 px-5 py-3 text-xs tracking-[0.28em] uppercase text-[#FDFBF7] hover:border-[#D4A373] hover:text-[#D4A373] transition-colors"
              data-testid="curated-gallery-see-all"
            >
              <Images className="w-4 h-4" strokeWidth={1.5} />
              {seeAllLabel}
            </button>
          </div>
        ) : null}
      </div>

      {lightboxIdx !== null ? (
        <Lightbox
          images={images}
          startIdx={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          title={hotelName || title}
        />
      ) : null}
    </section>
  );
};

export default CuratedGallerySection;
